import {CustomFieldHelper} from "bukku/shared/CustomFieldHelper";
import {t} from '@lingui/macro';
import {TransactionFileHelper} from "../../../components/LightGallery/TransactionFilesLightGallery/helper";
import { SubscriptionCheck } from "../../../components/Access/helper/subscription/SubscriptionCheck";
import { SubscriptionScopeKey } from "../../../components/Access/helper/subscription/enum/SubscriptionScopeKey";
import ContactActions from 'bukku/redux/Contact/actions';
import {store} from 'bukku/redux/store';
import { debounce } from 'bukku/helpers/debouncer';
import { ContactEntityType } from "../../../enums/ContactEntityType";
import { myInvoisFieldValidAndFilledValidator } from "./myInvoisValidator";
import { PermissionCheck } from "../../../components/Access/helper/permission/PermissionCheck";
import { PermissionKey } from "../../../components/Access/helper/permission/enum/PermissionKey";
import { PermissionV2 } from "../../../components/Access/helper/permission/enum/PermissionV2";

const { updateContactMyInvoisFormField } = ContactActions;

export const ContactTypes = {
    customer: 'customer',
    supplier: 'supplier',
    employee: 'employee',
};

export const ContactCategory = {
    DEBTOR: 'DEBTOR',
    CREDITOR: 'CREDITOR',
};

export const ContactCategoryToTypes = {
    DEBTOR: [ContactTypes.customer],
    CREDITOR: [ContactTypes.supplier, ContactTypes.employee],
};

export const ContactTypeInfo = {
    customer: { key: 'customer', label: t`Customer`, category: 'DEBTOR' },
    supplier: { key: 'supplier', label: t`Supplier`, category: 'CREDITOR' },
    employee: { key: 'employee', label: t`Employee`, category: 'CREDITOR' },
    // only used in helper
    supplier_employee: { key: 'supplier_employee', label: t`Supplier/Employee`, category: 'CREDITOR' },
};

/**
 * Returns Array of ContactTypeInfo Objects (eg: [{label: 'Customer', category: 'DEBTOR'}, {...}])
 * or with mapKey, reurns [String]
 * NOTE: Internationalization -- use withI18n() for files that invoke this function.
 * @param {[String]} contact_types ['contact', 'supplier', 'employee']
 * @param {String} mapKey 'label' or 'category'. Maps the array of object into array
 */
export const getContactTypeInfos = (contact_types, mapKey) => {
    const typeInfos = [];

    if (contact_types.includes(ContactTypes.supplier) && contact_types.includes(ContactTypes.employee)) {
        const haveCustomer = contact_types.indexOf('customer') !== -1;
        if (haveCustomer)
            typeInfos.push(ContactTypeInfo.customer);
        typeInfos.push(ContactTypeInfo.supplier_employee);
    }
    else {
        if (contact_types.includes(ContactTypes.customer))
            typeInfos.push(ContactTypeInfo.customer);
        if (contact_types.includes(ContactTypes.supplier))
            typeInfos.push(ContactTypeInfo.supplier);
        if (contact_types.includes(ContactTypes.employee))
            typeInfos.push(ContactTypeInfo.employee);
    }

    if (mapKey)
        return typeInfos.map(infoObject => infoObject[mapKey]);

    return typeInfos;
};

export const checkContactTypeReadable = contact => {
    const { types } = contact;
    const canReadContact = PermissionCheck.withKeys(PermissionKey.contact).hasPermissionTo(PermissionV2.read);
    const canReadContactType = {
        customer: PermissionCheck.withKeys(PermissionKey.contact_customer).hasPermissionTo(PermissionV2.read),
        supplier: PermissionCheck.withKeys(PermissionKey.contact_supplier).hasPermissionTo(PermissionV2.read),
        employee: PermissionCheck.withKeys(PermissionKey.contact_employee).hasPermissionTo(PermissionV2.read),
    };

    if (canReadContact)
        return true;

    return types.some(type => canReadContactType[type]);
};

/**
 *
 * @param {string[]} types
 * @returns {
 *  DEBTOR: boolean,
 *  CREDITOR: boolean
 * }
 */
export const checkContactTypeStatementReadable = types => {
    const canReadContact = PermissionCheck.withKeys(PermissionKey.contact).hasPermissionTo(PermissionV2.read);
    const canReadContactType = {
        customer: PermissionCheck.withKeys(PermissionKey.contact_customer).hasPermissionTo(PermissionV2.read),
        supplier: PermissionCheck.withKeys(PermissionKey.contact_supplier).hasPermissionTo(PermissionV2.read),
        employee: PermissionCheck.withKeys(PermissionKey.contact_employee).hasPermissionTo(PermissionV2.read),
    };
    const isContactStatementReadable = {
        DEBTOR: types.includes(ContactTypes.customer) && (canReadContactType.customer || canReadContact),
        CREDITOR: (
            // OR logic: as long as one of supplier or employee is readable, CREDITOR statemment is readable
            types.some(type => [ContactTypes.supplier, ContactTypes.employee].includes(type)) &&
            (
                (canReadContactType.supplier || canReadContactType.employee || canReadContact)
            )
        ),
        // AND logic:
        // (types.includes(ContactTypes.customer) && (canReadContactType.customer || canReadContact)) ||
        //       (
        // types.includes(ContactTypes.supplier) && (canReadContactType.supplier || canReadContact)) ||
        //       (types.includes(ContactTypes.employee) && (canReadContactType.employee || canReadContact)),
    };

    return isContactStatementReadable;
};

export const addStatementReadableFlag = contact => {
    const {
        types,
    } = contact;

    const {
        DEBTOR,
        CREDITOR,
    } = checkContactTypeStatementReadable(types);

    return {
        ...contact,
        isStatementPermitted: DEBTOR || CREDITOR,
        isDebtorStatementPermitted: DEBTOR,
        isCreditorStatementPermitted: CREDITOR,
    };
};

/**
 * To be used in drawers, show the statement type radio button only if there are multiple types in selected contacts
 * @param {Contact & { isCreditorStatementPermitted: boolean; isDebtorStatementPermitted: boolean; }} selectedContacts
 * @returns {Array<{label: string, category: ContactCategory}>} could be empty array if no valid type (eg: no permission)
 */
export const getValidStatementTypes = selectedContacts => {
    const parsedContacts = selectedContacts.map(addStatementReadableFlag);

    const hasSomeDebtor = parsedContacts.some(c => c.isDebtorStatementPermitted);
    const hasSomeCreditor = parsedContacts.some(c => c.isCreditorStatementPermitted);
    const types = [];
    if (hasSomeDebtor)
        types.push({ label: t`Customer`, category: ContactCategory.DEBTOR });
    if (hasSomeCreditor) {
        const hasSupplierType = parsedContacts.some(c => c.types.includes(ContactTypes.supplier));
        const hasEmployeeType = parsedContacts.some(c => c.types.includes(ContactTypes.employee));
        if (hasSupplierType && hasEmployeeType)
            types.push({ label: t`Supplier/Employee`, category: ContactCategory.CREDITOR });
        else if (hasSupplierType)
            types.push({ label: t`Supplier`, category: ContactCategory.CREDITOR });
        else if (hasEmployeeType)
            types.push({ label: t`Employee`, category: ContactCategory.CREDITOR });
    }
    return types;
};

/**
 * @DEPRECATED Oct 1, 2025 - use getValidStatementTypes() instead
 * Returns Array of ContactTypeInfo Objects (eg: [{label: 'Customer', category: 'DEBTOR'}, {...}])
 * NOTE: Internationalization -- use withI18n() for files that invoke this function.
 * @param {[{contact}]} contacts
 */
// export const getReducedContactTypeInfo = contacts => {
//     // ['supplier',   'employee','customer',   'supplier','employee']  (spaces to show different contact)
//     const arrayOfTypes = contacts.flatMap(c => c.types);

//     return getContactTypeInfos(arrayOfTypes);
// };

export const isContactTypePermitted = (type, permissionLevel) => {
    const contactPermission = PermissionCheck.withKeys(PermissionKey.contact);
    const permissionMap = {
        customer: PermissionCheck.withKeys(PermissionKey.contact_customer),
        supplier: PermissionCheck.withKeys(PermissionKey.contact_supplier),
        employee: PermissionCheck.withKeys(PermissionKey.contact_employee),
    };

    return permissionMap[type].hasPermissionTo(permissionLevel) ||
        contactPermission.hasPermissionTo(permissionLevel);
};

/**
 * Check if contact types are permitted based on permission level.
 * @param {*} types ['customer', 'supplier', 'employee']
 * @param {*} permissionLevel PermissionV2.create | PermissionV2.read | PermissionV2.update | PermissionV2.delete
 * @param {*} checkMethod "OR" | "AND"
 * @returns
 */
export const isContactTypesPermitted = (types, permissionLevel, checkMethod = "OR") => {
    if (checkMethod === "AND")
        return types.every(type => isContactTypePermitted(type, permissionLevel));

    return types.some(type => isContactTypePermitted(type, permissionLevel));
};

const populateContactMyInvoisFormFieldInRedux = (name, value) => {
    store.dispatch(updateContactMyInvoisFormField(name, value));
};

// Create a debounced version of the function
const debouncedPopulateContactMyInvoisFormFieldInRedux = debounce(
    populateContactMyInvoisFormFieldInRedux, 300,
);

const checkShouldValidateTinRegNoWithApi = form => {
    const currentEntityType = form?.getFieldValue('entity_type');

    const isIndividual = [
        ContactEntityType.MALAYSIAN_INDIVIDUAL.key,
        ContactEntityType.FOREIGN_INDIVIDUAL.key,
    ].includes(currentEntityType);

    const isExemptedGroup = [
        ContactEntityType.GENERAL_PUBLIC.key,
        ContactEntityType.FOREIGN_COMPANY.key,
        ContactEntityType.EXEMPTED_PERSON.key,
    ].includes(currentEntityType);

    // if malaysian/foreign individual, (reg_no must be filled, reg_no_type must be filled) OR (tax_id_no) must be filled
    const individualContactRegNoAndTypeOverride = (
        // for reg_no + type requirement to be overridden, tax_id_no has to be valid, and reg_no if has value, has to obey validation rules too
        isIndividual
        && myInvoisFieldValidAndFilledValidator(form?.getFieldValue('tax_id_no'), 'tax_id_no', form)
        && !form?.getFieldError(['reg_no']).length
    );

    const individualContactTaxIdOverride = (
        // for tax_id_no requirement to be overridden, reg_no + type has to be valid, and tax_id_no if has value, has to obey validation rules too
        isIndividual
        && myInvoisFieldValidAndFilledValidator(form?.getFieldValue('reg_no'), 'reg_no', form)
        && myInvoisFieldValidAndFilledValidator(form?.getFieldValue('reg_no_type'), 'reg_no_type', form)
        && !form?.getFieldError(['tax_id_no']).length
    );

    const isAllFilledAndValidated = (
        myInvoisFieldValidAndFilledValidator(form?.getFieldValue('reg_no_type'), 'reg_no_type', form) &&
        myInvoisFieldValidAndFilledValidator(form?.getFieldValue('reg_no'), 'reg_no', form) && // is the same as fieldsValid.reg_no && fieldsFilled.reg_no
        myInvoisFieldValidAndFilledValidator(form?.getFieldValue('tax_id_no'), 'tax_id_no', form)
    );

    if (currentEntityType === ContactEntityType.MALAYSIAN_COMPANY.key || isExemptedGroup)
        return isAllFilledAndValidated; // validate with api if all TIN and reg no + type are filled

    if (isIndividual) {
        if (isAllFilledAndValidated)
            return true;

        if (individualContactRegNoAndTypeOverride || individualContactTaxIdOverride)
            return false;

        return false;
    }

    return false;
};

export const ContactHelper = {
    normalizeData(data, addresses, contactPersons = [], parsedPhoneNumbers = "") {
        const hasContactGroupSub = SubscriptionCheck.withKeys(SubscriptionScopeKey["CONTACT.GROUP"]).hasSubscriptionTo();
        const hasMultiFormDesignSub =
            SubscriptionCheck.withKeys(SubscriptionScopeKey.MULTI_FORM_DESIGN).hasSubscriptionTo();

        const hasCustomFieldSub =
            SubscriptionCheck.withKeys(SubscriptionScopeKey.CUSTOM_FIELD).hasSubscriptionTo();

        const hasTagSub =
            SubscriptionCheck.withKeys(SubscriptionScopeKey.TAG).hasSubscriptionTo();

        const hasPriceLevelSub =
            SubscriptionCheck.withKeys(SubscriptionScopeKey.PRICE_LEVEL).hasSubscriptionTo();

        const newData = {
            other_name: data.other_name || null,
            contact_code: data.contact_code || null,
            first_name: data.first_name || null,
            last_name: data.last_name || null,
            legal_name: data.legal_name || null,
            reg_no: data.reg_no || null,
            old_reg_no: data.old_reg_no || null,
            reg_no_type: data.reg_no_type || null,
            sst_reg_no: data.sst_reg_no || null,
            tax_id_no: data.tax_id_no || null,
            entity_type: data.entity_type || null,
            types: data.types || null,
            receivable_account_id: data.receivable_account_id || null,
            payable_account_id: data.payable_account_id || null,
            group_ids: !hasContactGroupSub ? undefined : (data.group_ids || []),
            price_level_id: hasPriceLevelSub ? data.price_level_id || null : undefined,
            email: data.email || null,
            phone_no: parsedPhoneNumbers || null,
            default_term_id: data.default_term_id || null,
            default_currency_code: data.default_currency_code || null,
            default_income_account_id: data.default_income_account_id || null,
            default_expense_account_id: data.default_expense_account_id || null,
            default_myinvois_action: data.default_myinvois_action || null,
            default_location_id: data.default_location_id || null,
            debtor_credit_limit: (
                data.debtor_credit_limit || data.debtor_credit_limit === 0 ? data.debtor_credit_limit : null
            ),
            tag_ids: hasTagSub ? data.tag_ids || [] : undefined,
            fields: hasCustomFieldSub ? data.fields || [] : undefined,
            remarks: data.remarks || null,
            key: data.key || null,
            receive_monthly_statement: data.receive_monthly_statement,
            receive_invoice_reminder: data.receive_invoice_reminder,
            form_design_id: !hasMultiFormDesignSub ? undefined : data.form_design_id || null,
            files: [],
            addresses: [],
            contact_persons: [],
        };

        // If not customer
        const types = data.types || [];
        if (!types.find(type => type === 'customer')) {
            // eslint-disable-next-line no-param-reassign
            data.debtor_credit_limit = null;
        }

        addresses.forEach(address => {
            newData.addresses.push({
                id: address.id || null,
                name: address.name || null,
                street: address.street || null,
                city: address.city || null,
                state: address.state || null,
                postcode: address.postcode || null,
                country_code: address.country_code || null,
                is_default_shipping: address.is_default_shipping || false,
                is_default_billing: address.is_default_billing || false,
            });
        });

        if (newData.fields && hasCustomFieldSub) {
            newData.fields = newData.fields.map(field => ({
                id: field.id || null,
                field_id: field.field_id || null,
                name: field.name || null,
                value: field.value || null,
            }));
        }

        contactPersons.forEach(cp => {
            if (cp.first_name || cp.last_name || cp.is_default_billing || cp.is_default_shipping) {
                newData.contact_persons.push({
                    id: cp.id || null,
                    first_name: cp.first_name || null,
                    last_name: cp.last_name || null,
                    is_default_shipping: cp.is_default_shipping || false,
                    is_default_billing: cp.is_default_billing || false,
                });
            }
        });

        // Files
        TransactionFileHelper.normalizeData(newData, data.files);
        return newData;
    },

    prepareFormData(rawFormData, fields, files, contact) {
        const formData = {
            other_name: rawFormData.other_name,
            contact_code: rawFormData.contact_code,
            first_name: rawFormData.first_name,
            last_name: rawFormData.last_name,
            legal_name: rawFormData.legal_name,
            reg_no: rawFormData.reg_no,
            old_reg_no: rawFormData.old_reg_no,
            reg_no_type: rawFormData.reg_no_type,
            sst_reg_no: rawFormData.sst_reg_no,
            tax_id_no: rawFormData.tax_id_no,
            entity_type: rawFormData.entity_type,
            types: rawFormData.types,
            group_ids: rawFormData.group_ids,
            price_level_id: rawFormData.price_level_id,
            email: rawFormData.email,
            // phone_no: rawFormData.phone_no, // will be added in normalizeData
            // phone_no_code: rawFormData.phone_no_code,
            addresses: [],
            contact_persons: [],
            tag_ids: rawFormData.tag_ids,
            default_term_id: rawFormData.default_term_id,
            default_currency_code: rawFormData.default_currency_code,
            default_income_account_id: rawFormData.default_income_account_id,
            default_expense_account_id: rawFormData.default_expense_account_id,
            default_location_id: rawFormData.default_location_id,
            default_myinvois_action: rawFormData.default_myinvois_action,
            debtor_credit_limit: rawFormData.debtor_credit_limit,
            receive_monthly_statement: rawFormData.receive_monthly_statement,
            receive_invoice_reminder: rawFormData.receive_invoice_reminder,
            fields: [],
            remarks: rawFormData.remarks,
            key: rawFormData.key,
            form_design_id: rawFormData.form_design_id,
            files,
        };

        const types = rawFormData.types || [];

        if (types.indexOf('customer') !== -1)
            formData.receivable_account_id = rawFormData.receivable_account_id;
        if (types.indexOf('supplier') !== -1 ||
            types.indexOf('employee') !== -1)
            formData.payable_account_id = rawFormData.payable_account_id;

        // Fields
        CustomFieldHelper.prepareFormContact(rawFormData, formData, fields, contact);
        return formData;
    },

    initContact(contact, fields) {
        const formData = {
            ...contact,
            addresses: undefined,
            receivable_account_id: contact.receivable_account_id || undefined,
            payable_account_id: contact.payable_account_id || undefined,
            default_currency_code: contact.default_currency_code || undefined,
            default_term_id: contact.default_term_id || undefined,
            default_income_account_id: contact.default_income_account_id || undefined,
            default_expense_account_id: contact.default_expense_account_id || undefined,
            default_location_id: contact.default_location_id || undefined,
            default_myinvois_action: contact.default_myinvois_action || undefined,
            debtor_credit_limit: contact.debtor_credit_limit,
            price_level_id: contact.price_level_id || undefined,
            receive_monthly_statement: contact.receive_monthly_statement,
            receive_invoice_reminder: contact.receive_invoice_reminder,
            phone_no: contact.phone_no,
            // first_name: contact.billing_first_name,
            // last_name: contact.billing_last_name,
        };

        contact.contact_persons?.forEach((cp, idx) => {
            const default_settings = [];
            if (cp.is_default_shipping)
                default_settings.push('is_default_shipping');
            if (cp.is_default_billing)
                default_settings.push('is_default_billing');

            formData.contact_persons[idx].default_settings = default_settings;
            formData[`contact_person_${cp.key}`] = {
                ...formData.contact_persons[idx],
            };
        });

        contact.addresses.forEach(address => {
            const default_settings = [];
            if (address.is_default_shipping)
                default_settings.push('is_default_shipping');
            if (address.is_default_billing)
                default_settings.push('is_default_billing');

            formData[`address_${address.key}`] = {
                name: address.name || null,
                street: address.street || null,
                city: address.city || null,
                state: address.state || null,
                postcode: address.postcode || null,
                country_code: address.country_code || null,
                default_settings,
            };
        });

        CustomFieldHelper.initFormContact(formData, fields, contact);
        return formData;
    },
    // to invoke onchange of input fields using event value and target name
    populateContactMyInvoisFormFieldInRedux,
    debouncedPopulateContactMyInvoisFormFieldInRedux,
    checkShouldValidateTinRegNoWithApi,
};

export const getPostcodeRules = (countryFieldName = "country_code") => [
    ({ getFieldValue }) => ({
        validator(_, value) {
            const countryCode = getFieldValue(countryFieldName);
            const maxLength = countryCode === "MY" ? 5 : 10;
            if (value && value.length > maxLength) {
                if (countryCode === "MY") {
                    return Promise.reject(
                        new Error(
                            t`Malaysia postcode cannot be longer than 5 characters.`,
                        ),
                    );
                }

                return Promise.reject(
                    new Error(
                        t`Postcode cannot be longer than ${maxLength} characters.`,
                    ),
                );
            }
            return Promise.resolve();
        },
    }),
];
